import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import Radio, { RadioProps } from '@mui/material/Radio';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup, { RadioGroupProps } from '@mui/material/RadioGroup';
import { colors } from 'common/constants';
import { Box, styled } from '@mui/material';

type Props = RadioGroupProps & {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- options can be any
    options: { label: string; value: any; image?: string; type?: number }[];
    label?: string;
    spacing?: number;
    helperText?: React.ReactNode;
};

const BpIcon = styled('span')(({ theme }) => ({
    borderRadius: '50%',
    width: 18,
    height: 18,
    boxShadow:
        'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
    backgroundColor: '#f5f8fa',
    border: '1px solid black',

    '.Mui-focusVisible &': {
        outline: '2px auto rgba(19,124,189,.6)',
        outlineOffset: 2,
    },
    'input:hover ~ &': {
        backgroundColor: '#ebf1f5',
        ...theme.applyStyles('dark', {
            backgroundColor: '#30404d',
        }),
    },
    'input:disabled ~ &': {
        boxShadow: 'none',
        background: 'rgba(206,217,224,.5)',
        ...theme.applyStyles('dark', {
            background: 'rgba(57,75,89,.5)',
        }),
    },
    ...theme.applyStyles('dark', {
        boxShadow: '0 0 0 1px rgb(16 22 26 / 40%)',
        backgroundColor: '#394b59',
        backgroundImage:
            'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))',
    }),
}));

const BpCheckedIcon = styled(BpIcon)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    '&::before': {
        display: 'block',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundSize: '80% 80%',
        content: '""',
        width: '100%',
        height: '100%',
    },
    'input:checked ~ &': { border: 'none' },
    'input:hover ~ &': {
        backgroundColor: theme.palette.primary.main,
    },
}));

export function BpRadio(props: RadioProps) {
    return (
        <Radio
            disableRipple
            color="default"
            checkedIcon={<BpCheckedIcon />}
            icon={<BpIcon />}
            {...props}
        />
    );
}

export default function BaseRadioGroupPayment({
    row,
    name,
    label,
    options,
    spacing,
    helperText,
    ...other
}: Props) {
    const { control, watch } = useFormContext();

    const labelledby = label ? `${name}-${label}` : '';

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <FormControl
                    component="fieldset"
                    sx={{
                        width: '100%',
                    }}
                >
                    {label && (
                        <FormLabel
                            component="legend"
                            id={labelledby}
                            sx={{ typography: 'body2' }}
                        >
                            {label}
                        </FormLabel>
                    )}

                    <RadioGroup
                        {...field}
                        aria-labelledby={labelledby}
                        row={row}
                        {...other}
                    >
                        {options.map((option) => (
                            <Box key={option.value}>
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    className={
                                        watch(name) === option.value
                                            ? 'selected'
                                            : ''
                                    }
                                    control={
                                        <BpRadio onClick={field.onChange} />
                                    }
                                    label={
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                            }}
                                        >
                                            {option.image && (
                                                <img
                                                    src={option.image}
                                                    alt={option.label}
                                                />
                                            )}
                                            {<b>{option.label}</b>}
                                        </Box>
                                    }
                                    sx={{
                                        ...(row && {
                                            mr: 0,
                                            '&:not(:last-of-type)': {
                                                mr: spacing || 2,
                                            },
                                        }),
                                    }}
                                />
                            </Box>
                        ))}
                    </RadioGroup>

                    {(!!error || helperText) && (
                        <FormHelperText
                            error={!!error}
                            sx={{
                                fontSize: '14px',
                                marginLeft: 0,
                                color: `${colors.textError} !important`,
                                fontWeight: '400',
                            }}
                        >
                            {error ? error?.message : helperText}
                        </FormHelperText>
                    )}
                </FormControl>
            )}
        />
    );
}
